import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { fmtAnios, type EscenarioSaldo, type ResumenServicioEmpleado } from './types.js';

interface ResultadoBackfillTodos {
  usuariosRevisados: number;
  allocationsCreadas: number;
}

// Prompt BB-4(RH) — Pantalla 1: tabla agregada de todos los empleados, mismo
// patrón de fila clicleable que Días Festivos/Tipos de Ausencia (lista+
// detalle, sin modales). Visible a todos los autenticados (nav sin roles=),
// igual criterio que "Ausencias" — cualquiera puede ver su propia antigüedad
// y la del resto del organigrama.
export function AniosServicioPage() {
  const { get, post } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toasts, showToast, dismiss } = useToasts();
  // Leadsphere = saldo real (fecha de ingreso LS). Nómina = saldo paralelo
  // que RH reporta a nómina (desde fecha de ingreso nómina).
  const [escenario, setEscenario] = useState<EscenarioSaldo>('leadsphere');

  const { data: resumen, isLoading } = useQuery({
    queryKey: ['vacaciones-allocations-resumen-servicio'],
    queryFn: () => get<ResumenServicioEmpleado[]>('/vacaciones-allocations/resumen-servicio'),
  });

  // Prompt BK-03 — botón "Recalcular vacaciones" (root/head): dispara
  // detectarNuevosAniversarios() bajo demanda para TODOS los usuarios en vez
  // de esperar al cron de las 3am — pensado para usarse justo después de
  // sembrar/editar usuarios y tramos de antigüedad en bulk.
  const puedeRecalcular = user?.rol === 'root' || user?.rol === 'head';

  const recalcularMutation = useMutation({
    mutationFn: () => post<ResultadoBackfillTodos>('/vacaciones-allocations/backfill-todos'),
    onSuccess: async (resultado) => {
      showToast(
        'success',
        `${resultado.allocationsCreadas} allocation(s) nueva(s) creada(s) en ${resultado.usuariosRevisados} usuario(s) revisado(s).`,
      );
      await queryClient.invalidateQueries({ queryKey: ['vacaciones-allocations-resumen-servicio'] });
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible recalcular las vacaciones.');
    },
  });

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Años de Servicio</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Antigüedad y balance de vacaciones por empleado</div>
        </div>
        {puedeRecalcular && (
          <Button
            variant="secondary"
            onClick={() => recalcularMutation.mutate()}
            disabled={recalcularMutation.isPending}
          >
            {recalcularMutation.isPending ? 'Recalculando…' : 'Recalcular vacaciones'}
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Button size="sm" variant={escenario === 'leadsphere' ? 'primary' : 'secondary'} onClick={() => setEscenario('leadsphere')}>
          Leadsphere
        </Button>
        <Button size="sm" variant={escenario === 'nomina' ? 'primary' : 'secondary'} onClick={() => setEscenario('nomina')}>
          Nómina
        </Button>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Antigüedad</th>
                <th>Antigüedad nómina</th>
                <th>Vacaciones totales{escenario === 'nomina' ? ' (nómina)' : ''}</th>
                <th>Tomadas</th>
                <th>Restantes</th>
              </tr>
            </thead>
            <tbody>
              {!resumen || resumen.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={6}>
                    {isLoading ? 'Cargando…' : 'No hay empleados registrados'}
                  </td>
                </tr>
              ) : (
                resumen.map(r => {
                  const saldo = escenario === 'leadsphere' ? r : r.nomina;
                  return (
                    <tr key={r.usuarioId}>
                      <td>
                        <span className="tk-folio" onClick={() => navigate(`/anios-servicio/${r.usuarioId}`)}>
                          {r.nombre}
                        </span>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{fmtAnios(r.antiguedadAnios)}</td>
                      <td style={{ color: 'var(--muted)' }}>{fmtAnios(r.nomina?.antiguedadAnios)}</td>
                      <td>{saldo ? saldo.diasTotales : '—'}</td>
                      <td>{saldo ? saldo.diasTomados : '—'}</td>
                      <td style={{ color: saldo && saldo.diasRestantes < 0 ? 'var(--danger)' : undefined }}>
                        {saldo ? saldo.diasRestantes : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
